import { Link } from "react-router";

import { GuideArticle, Section } from "../GuideShell";

export function RoundSynthesisPage() {
  return (
    <GuideArticle
      slug="round-synthesis"
      title="Interview round synthesis"
      intro={
        <p>
          When an interview round completes, Rarion analyses the round's contributions in two
          phases: first an objective evidence-gathering pass against the role blueprint, then a
          balanced assessment that weights interviewer feedback over the transcript. The synthesis
          never recommends an outcome — it shows how well the candidate demonstrated fit, and where
          the evidence is incomplete. The recruiter decides.
        </p>
      }
    >
      <Section id="evidence" title="Phase 1 — evidence gathering">
        <p>
          When an interview step is marked complete, the round-synthesis workflow reads all of that
          round's interview contributions — notes, transcripts, and assessment-form responses — and
          matches what was actually said or observed to the blueprint's capability dimensions. This
          pass is deliberately judgement-free: it extracts evidence, it does not score it.
        </p>
        <p>
          The step's coverage plan defines the focus: dimensions the interview was planned to probe
          are searched hardest, while clear incidental evidence on other dimensions is still
          captured. Every evidence entry records its source — which contribution it came from,
          whether that was a transcript or interviewer-authored feedback, and who contributed it.
        </p>
      </Section>

      <Section id="assessment" title="Phase 2 — balanced assessment">
        <p>
          The second pass scores the round from the gathered evidence alone — it never re-reads the
          raw transcript, so it cannot take the candidate's self-presentation at face value. Two
          rules shape the result:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Interviewer feedback outweighs the transcript.</strong> Notes and
            assessment-form submissions come from people who were in the room; the transcript is
            useful input but is not sufficient on its own for a top score.
          </li>
          <li>
            <strong>Only evidenced dimensions are scored.</strong> A dimension without enough
            evidence to score honestly is left unscored rather than guessed at.
          </li>
        </ul>
        <p>
          The result is a versioned <em>InterviewRoundSynthesisVersion</em> containing per-dimension
          scores with{" "}
          <Link to="/help/anchors" className="text-link hover:underline">
            evidence
          </Link>
          , a balanced round summary, and areas to probe in future rounds.
        </p>
      </Section>

      <Section id="gaps" title="Coverage gaps & follow-up questions">
        <p>
          Any in-focus dimension the round failed to produce evidence for is flagged as a coverage
          gap, with a concise note on what could not be determined and suggested follow-up questions
          to put to the candidate. Gaps flow forward into the next round's interview pack, so later
          interviews can close them deliberately.
        </p>
      </Section>

      <Section id="no-outcome" title="Why there is no recommendation">
        <p>
          The synthesis deliberately carries no advance/reject posture. The system gathers and
          weighs evidence; the recruiter judges what it means and acts via stage transitions. This
          is the same principle behind{" "}
          <Link to="/help/score-bands" className="text-link hover:underline">
            score bands
          </Link>
          : Rarion never designates hireability.
        </p>
      </Section>
    </GuideArticle>
  );
}
